/**
 * Per-iteration run selector — n8n's "Run 3 of 3 (1 item)".
 *
 * Shared by the THREE surfaces that show a node's I/O (the drawer's Input and
 * Output panes + the bottom Logs dock), because the question "which iteration
 * am I looking at?" is the same everywhere and three copies would drift.
 *
 * Only render it when the trace carries run windows (the node ran > once) —
 * `runSlicesOf` returns null otherwise, so a plain node's pane is untouched.
 */

import { AlertCircle, CheckCircle2, Repeat2 } from "lucide-react";

import { cn } from "@/lib/cn";
import { type NodeRunSlice, runsAreCapped, runsTotal } from "@/lib/workflows/nodeRuns";

export interface RunSelectorProps {
  runs: NodeRunSlice[];
  logOutputs: Record<string, unknown> | null | undefined;
  /** null = "Tutte" (the merged batch — the historical view). */
  selected: number | null;
  onSelect: (run: number | null) => void;
  /** Which side we're paging — only changes the item-count label. */
  side?: "in" | "out";
  className?: string;
}

export function RunSelector({
  runs,
  logOutputs,
  selected,
  onSelect,
  side = "out",
  className,
}: RunSelectorProps) {
  const total = runsTotal(logOutputs, runs);
  const capped = runsAreCapped(logOutputs, runs);
  const countOf = (r: NodeRunSlice) => (side === "in" ? r.items_in : r.items_out);
  const totalItems = runs.reduce((n, r) => n + countOf(r), 0);
  const active = runs.find((r) => r.run === selected) ?? null;
  const failed = runs.filter((r) => r.status === "failed").length;

  return (
    <div className={cn("flex flex-wrap items-center gap-1.5 text-[11px]", className)}>
      <Repeat2 className="h-3 w-3 shrink-0 text-muted-foreground" strokeWidth={1.5} />
      <span className="text-muted-foreground">Run</span>
      <select
        value={selected === null ? "" : String(selected)}
        onChange={(e) => onSelect(e.target.value === "" ? null : Number(e.target.value))}
        className={cn(
          "min-w-0 rounded border bg-background px-1.5 py-0.5 text-[11px] transition",
          active
            ? "border-accent/60 text-foreground"
            : "border-border/60 text-muted-foreground hover:text-foreground",
        )}
        aria-label="Iterazione"
      >
        <option value="">Tutte ({totalItems} item)</option>
        {runs.map((r) => (
          <option key={r.run} value={r.run}>
            {/* 1-based for humans; `run` is the 0-based iteration index. */}
            {r.run + 1} di {total} ({countOf(r)} item){r.status === "failed" ? " — errore" : ""}
          </option>
        ))}
      </select>
      {active && (
        <span className="flex shrink-0 items-center gap-1">
          {active.status === "failed" ? (
            <AlertCircle className="h-3 w-3 text-danger" strokeWidth={1.5} />
          ) : (
            <CheckCircle2 className="h-3 w-3 text-success" strokeWidth={1.5} />
          )}
          <span className="font-mono text-muted-foreground">{active.duration_ms}ms</span>
        </span>
      )}
      {failed > 0 && selected === null && (
        // Surface the failures the merged view hides — they're WHY you opened this.
        <button
          type="button"
          onClick={() => onSelect(runs.find((r) => r.status === "failed")!.run)}
          className="rounded px-1 text-danger underline-offset-2 hover:underline"
        >
          {failed} {failed === 1 ? "fallita" : "fallite"}
        </button>
      )}
      {capped && (
        // Don't let a truncated list read as "the other iterations didn't run".
        <span className="text-muted-foreground" title={`Mostrate ${runs.length} di ${total}`}>
          · troncato {runs.length}/{total}
        </span>
      )}
    </div>
  );
}
